import { useState, useEffect, useCallback } from 'react';
import { supabase, optimizedQuery, withCache } from '../lib/supabase';

interface Product {
  id: string;
  name: string;
  main_image: string;
  base_price: number;
  code?: string;
  barcode?: string;
  description?: string;
  on_sale?: boolean;
  sale_price?: number;
  company?: string[];
  // Categorías asociadas (N:M)
  categories?: { name: string; }[];
}

interface SearchOptions {
  query?: string;
  categoryId?: string;
  minPrice?: number;
  maxPrice?: number;
  inStock?: boolean;
  onSaleOnly?: boolean;
  companyFilter?: string[];
  limit?: number;
  offset?: number;
}

export function useProductSearch(initialQuery: string = '', limit: number = 5) {
  const [searchTerm, setSearchTerm] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchOptions, setSearchOptions] = useState<SearchOptions>({});

  // Búsqueda avanzada con múltiples campos
  const searchProductsAdvanced = async (options: SearchOptions) => {
    try {
      const { data, error } = await supabase.rpc('search_products_advanced', {
        search_query: options.query || '',
        p_category_id: options.categoryId || null,
        min_price: options.minPrice || null,
        max_price: options.maxPrice || null,
        in_stock: options.inStock !== false,
        on_sale_only: options.onSaleOnly || false,
        company_filter: options.companyFilter || null,
        limit_count: options.limit || 20,
        offset_count: options.offset || 0
      });

      if (error) throw error;
      
      return data || [];
    } catch (err) {
      console.error('Error in advanced search:', err);
      return [];
    }
  };

  // Búsqueda básica para sugerencias (mantiene compatibilidad)
  const searchProductsBasic = async (query: string, limit: number) => {
    try {
      // Búsqueda en múltiples campos usando OR
      const { data, error } = await supabase
        .from('products')
        .select('id, name, main_image, base_price, code, barcode, description, on_sale, sale_price, company, categories:product_categories(category:categories(name))')
        .eq('status', 'active')
        .or(`name.ilike.%${query}%,code.ilike.%${query}%,barcode.ilike.%${query}%,description.ilike.%${query}%`)
        .limit(limit);

      if (error) throw error;
      
      const mapped = (data || []).map((item: any): Product => ({
        id: item.id,
        name: item.name,
        main_image: item.main_image,
        base_price: item.base_price,
        code: item.code,
        barcode: item.barcode,
        description: item.description,
        on_sale: item.on_sale,
        sale_price: item.sale_price,
        company: item.company,
        categories: item.categories?.map((pc: any) => pc.category) || []
      }));
      
      return mapped;
    } catch (err) {
      console.error('Error searching products:', err);
      return [];
    }
  };

  // Búsqueda inteligente que combina múltiples campos
  const searchProductsIntelligent = async (query: string, limit: number) => {
    if (!query.trim()) return [];

    try {
      // Primero intentar búsqueda avanzada
      const advancedResults = await searchProductsAdvanced({
        query: query.trim(),
        limit: limit
      });

      if (advancedResults.length > 0) {
        return advancedResults.map((item: any): Product => ({
          id: item.id,
          name: item.name,
          main_image: item.main_image,
          base_price: item.base_price,
          code: item.code,
          barcode: item.barcode,
          description: item.description,
          on_sale: item.on_sale,
          sale_price: item.sale_price,
          company: item.company,
          categories: []
        }));
      }

      // Si no hay resultados avanzados, usar búsqueda básica
      return await searchProductsBasic(query, limit);
    } catch (err) {
      console.error('Error in intelligent search:', err);
      return await searchProductsBasic(query, limit);
    }
  };

  useEffect(() => {
    const searchProducts = async () => {
      if (!searchTerm.trim()) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        const results = await searchProductsIntelligent(searchTerm, limit);
        setSuggestions(results);
      } catch (err) {
        console.error('Error searching products:', err);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    // Aumentar el debounce para reducir llamadas excesivas
    const debounceTimer = setTimeout(searchProducts, 500);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, limit]); // Solo dependencias necesarias

  // Función para búsqueda avanzada desde componentes
  const searchAdvanced = useCallback(async (options: SearchOptions) => {
    setLoading(true);
    try {
      const results = await searchProductsAdvanced(options);
      return results;
    } catch (err) {
      console.error('Error in advanced search:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Función para obtener productos relacionados - memoizada correctamente
  const getRelatedProducts = useCallback(async (productId: string, limit: number = 8) => {
    try {
      // Validar parámetros
      if (!productId || typeof productId !== 'string') {
        console.warn('getRelatedProducts: productId inválido', productId);
        return [];
      }

      if (!limit || limit <= 0 || limit > 50) {
        limit = 8; // valor por defecto seguro
      }

      const cacheKey = `related:${productId}:${limit}`;
      console.log(`Buscando productos relacionados para: ${cacheKey}`);

      const mapped = await withCache(cacheKey, async () => {
        console.log(`Realizando consulta RPC para: ${productId} (limit: ${limit})`);

        const { data, error } = await supabase.rpc('get_related_products', {
          product_id: productId,
          limit_count: limit
        });

        if (error) {
          console.error('Error en RPC get_related_products:', error);
          throw error;
        }

        const result = (data || []).map((item: any): Product => ({
          id: item.id,
          name: item.name,
          main_image: item.main_image,
          base_price: item.base_price,
          code: item.code,
          barcode: item.barcode,
          description: item.description,
          on_sale: item.on_sale,
          sale_price: item.sale_price,
          company: item.company,
          categories: []
        }));

        console.log(`Encontrados ${result.length} productos relacionados para ${productId}`);
        return result;
      }, 300); // Aumentar TTL a 5 minutos para productos relacionados

      return mapped;
    } catch (err) {
      console.error('Error getting related products:', err);
      return [];
    }
  }, []); // Sin dependencias para evitar recreaciones

  return {
    searchTerm,
    setSearchTerm,
    suggestions,
    loading,
    searchAdvanced,
    getRelatedProducts,
    searchOptions,
    setSearchOptions
  };
} 
